import { useState } from 'react';
import toast from 'react-hot-toast';

import { BackfillButton } from '../components/BackfillButton';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { api } from '../lib/api';

interface RollupState {
  isRunning: boolean;
  lastSuccessAt: Date | null;
}

export function AdminPage() {
  const { user } = useAuth();
  const { entitlements, isLoading: entitlementsLoading, refetch } = useEntitlements();
  const [rollupState, setRollupState] = useState<RollupState>({ isRunning: false, lastSuccessAt: null });

  const handleRunRollupAll = async () => {
    try {
      setRollupState((prev) => ({ ...prev, isRunning: true }));
      const response = await api.rollUpAll();
      const executionTime = new Date(response.executedAt);
      setRollupState({ isRunning: false, lastSuccessAt: executionTime });
      toast.success('rollUpAll executed successfully');
    } catch (error: unknown) {
      console.error('rollUpAll execution failed:', error);
      setRollupState((prev) => ({ ...prev, isRunning: false }));
      toast.error('Failed to run rollUpAll. Check logs for details.');
    }
  };

  if (entitlementsLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse rounded-md bg-gray-100 p-6" />
        <div className="animate-pulse rounded-md bg-gray-100 p-6" />
        <div className="animate-pulse rounded-md bg-gray-100 p-6" />
      </div>
    );
  }

  if (!entitlements.has('admin')) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Tools</h1>
          <p className="mt-1 text-sm text-gray-600">
            Administrative utilities are restricted to users with the admin entitlement.
          </p>
        </div>

        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex">
            <div className="flex-shrink-0 text-2xl text-red-500">🔒</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-900">Access Restricted</h3>
              <p className="mt-2 text-sm text-red-700">
                Your account does not have the admin entitlement. Please contact support if you believe this is an error.
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-4 inline-flex items-center rounded-md border border-red-200 bg-white px-3 py-1 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50"
              >
                Retry Entitlement Check
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">Admin Operations</h1>
        <p className="text-sm text-gray-600">
          High-sensitivity tools and operations reserved for admin-entitled users.
        </p>
        <p className="text-xs text-gray-500">
          Signed in as {user?.email ?? 'unknown user'}
        </p>
      </section>

      <section className="space-y-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h2 className="text-lg font-semibold text-blue-900">Data Processing</h2>
          <p className="text-sm text-blue-800">
            Aggregate raw feedback into analytics metrics across all sites.
          </p>
        </div>

        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-yellow-900">rollUpAll Aggregation</h3>
              <p className="text-sm text-yellow-800">
                Execute the global roll-up routine to process every unprocessed vote and comment.
                Run during incidents if scheduled aggregation is lagging.
              </p>
              {rollupState.lastSuccessAt ? (
                <p className="text-xs text-yellow-700">
                  Last run: {rollupState.lastSuccessAt.toLocaleString()}
                </p>
              ) : (
                <p className="text-xs text-yellow-700">The aggregation has not been triggered manually yet.</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => void handleRunRollupAll()}
              disabled={rollupState.isRunning}
              className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors ${
                rollupState.isRunning ? 'cursor-not-allowed bg-gray-400' : 'bg-yellow-600 hover:bg-yellow-700'
              }`}
            >
              {rollupState.isRunning ? 'Running…' : 'Run rollUpAll'}
            </button>
          </div>
        </div>

        <BackfillButton />
      </section>
    </div>
  );
}

